import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["mainImage", "thumbnail"]

  connect() {
    this.registerEventListeners()
    // Preload only current and next image in the gallery
    this.preloadVisibleImages()
  }

  disconnect() {
    // Clean up when controller is disconnected
    this.unregisterEventListeners()
  }

  registerEventListeners() {
    // Ensure we're not registering duplicate listeners
    this.unregisterEventListeners()

    // Save bound methods to be able to remove them later
    this.boundHandleSelect = this.select.bind(this)

    // Add event listeners to thumbnails
    this.thumbnailTargets.forEach(thumbnail => {
      thumbnail.addEventListener('click', this.boundHandleSelect)
    })
  }

  unregisterEventListeners() {
    if (this.boundHandleSelect) {
      this.thumbnailTargets.forEach(thumbnail => {
        thumbnail.removeEventListener('click', this.boundHandleSelect)
      })
    }
  }

  select(event) {
    event.preventDefault()

    // Get the clicked thumbnail
    const thumbnail = event.currentTarget

    // Remove active state from all thumbnails
    this.thumbnailTargets.forEach(thumb => {
      thumb.classList.remove('border-h85-yellow-500')
      thumb.classList.add('border-transparent')
    })

    // Add active state to clicked thumbnail
    thumbnail.classList.remove('border-transparent')
    thumbnail.classList.add('border-h85-yellow-500')

    // Get the image within the thumbnail
    const thumbnailImg = thumbnail.querySelector('img')
    if (!thumbnailImg) return

    // Update the main image if we have one
    if (this.hasMainImageTarget) {
      const mainImage = this.mainImageTarget

      // Save the current src and alt attributes
      const currentSrc = mainImage.src
      const currentAlt = mainImage.alt

      // Only update if the source is different to avoid unnecessary reloads
      if (currentSrc !== thumbnailImg.src) {
        // Create a preloader to avoid flashing
        const preloader = new Image()
        preloader.onload = () => {
          mainImage.src = thumbnailImg.src
          mainImage.alt = thumbnailImg.alt || currentAlt
        }
        preloader.src = thumbnailImg.src
      }
    }

    // Preload the next image in sequence for smoother browsing
    this.preloadNextImage(thumbnail)
  }

  preloadVisibleImages() {
    // Just preload the first few images to avoid excessive memory usage
    const imagesToPreload = Math.min(2, this.thumbnailTargets.length)

    for (let i = 0; i < imagesToPreload; i++) {
      const thumbnailImg = this.thumbnailTargets[i]?.querySelector('img')
      if (thumbnailImg && thumbnailImg.src) {
        const preloader = new Image()
        preloader.src = thumbnailImg.src
      }
    }
  }

  preloadNextImage(currentThumbnail) {
    const currentIndex = this.thumbnailTargets.indexOf(currentThumbnail)
    const nextIndex = (currentIndex + 1) % this.thumbnailTargets.length

    if (nextIndex !== currentIndex) {
      const nextThumbnail = this.thumbnailTargets[nextIndex]
      const nextImg = nextThumbnail?.querySelector('img')

      if (nextImg && nextImg.src) {
        const preloader = new Image()
        preloader.src = nextImg.src
      }
    }
  }
}